import { ProductPropertiesSection } from './ProductPropertiesSection';
import { catalogPropertiesError } from './catalogPropertyFields';
import { useEffect, useRef } from 'react';
import { CatalogFormSection } from './CatalogFormSection';
import { useForm, FormProvider } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { AdminBootstrapDictionaries } from '../../../api/types';
import { CatalogProductFormState } from './catalogFormValues';
import { useCatalogProductSave } from './useCatalogProductEditor';
import { useCatalogFormSubmit } from './useCatalogFormSubmit';
import { useCatalogLeaveConfirmation } from './useCatalogLeaveConfirmation';
import { ProductIdentitySection } from './ProductIdentitySection';
import { ProductClassificationSection } from './ProductClassificationSection';
import { getAdminCatalogPath, getAdminCatalogProductDetailPath, getAdminCatalogProductEditPath } from '../adminRoutes';
import { CatalogEntityHeader } from './CatalogEntityHeader';
import { CatalogEditorActions } from './CatalogEditorActions';
import { Tabs } from '../../../components/Tabs';
import { useProductTab } from './useProductTab';
import { CatalogProductOffers } from './CatalogProductOffers';
import { CatalogProductMedia } from './CatalogProductMedia';

interface CatalogProductFormProps {
  initialValues: CatalogProductFormState;
  entityId?: number;
  dictionaries: AdminBootstrapDictionaries;
}

export function CatalogProductForm({ initialValues, entityId, dictionaries }: CatalogProductFormProps) {
  const { tab, setTab } = useProductTab();
  const form = useForm<CatalogProductFormState>({ defaultValues: initialValues });
  const mutation = useCatalogProductSave(entityId);
  const navigate = useNavigate();
  const isCreating = entityId === undefined;
  const shouldOpenPhotos = useRef(false);
  const isSaving = form.formState.isSubmitting || mutation.isPending;
  const rootError = form.formState.errors.root?.message;
  const submitCount = form.formState.submitCount;
  const checkedSubmitCount = useRef(0);
  useEffect(() => {
    if (checkedSubmitCount.current === submitCount) return;
    checkedSubmitCount.current = submitCount;
    if (submitCount > 0 && Object.keys(form.formState.errors).length > 0) setTab(form.formState.errors.product_properties ? 'properties' : 'main');
  }, [submitCount, form.formState.errors, setTab]);

  const confirmLeave = useCatalogLeaveConfirmation(form.formState.isDirty);
  const save = useCatalogFormSubmit(form, mutation.mutateAsync, (savedEntity) => {
    const path = shouldOpenPhotos.current ? `${getAdminCatalogProductEditPath(savedEntity.product_id)}#photos` : getAdminCatalogProductDetailPath(savedEntity.product_id);
    navigate(path, { state: { catalogSaved: true } });
  });
  const submit = (event: React.FormEvent<HTMLFormElement>): void => {
    const error = catalogPropertiesError(form.getValues('product_properties'));
    if (error) {
      event.preventDefault();
      form.setError('product_properties', { message: error });
      setTab('properties');
      return;
    }
    form.clearErrors('product_properties');
    void save(event);
  };
  const cancel = (): void => {
    if (!confirmLeave()) return;
    const path = entityId === undefined ? getAdminCatalogPath('products') : getAdminCatalogProductDetailPath(entityId);
    navigate(path);
  };
  const title = isCreating ? 'Новый товар' : initialValues.product_name;
  const subtitle = isCreating ? 'Создание товара' : `Редактирование товара · ${initialValues.product_sku}`;

  return (
    <FormProvider {...form}>
      <form className="catalog-editor-form" noValidate onSubmit={submit} aria-label="Форма товара">
        <CatalogEntityHeader kind="products" title={title} subtitle={subtitle} isEditing actions={<CatalogEditorActions isSaving={isSaving} isDirty={form.formState.isDirty} isCreating={isCreating} onCancel={cancel} />} />
        {rootError ? <p className="catalog-feedback catalog-feedback_error" role="alert">{rootError}</p> : null}
        <Tabs ariaLabel="Разделы товара" className="catalog-product-tabs" keepMounted value={tab} onChange={setTab} items={[
          { value: 'main', title: 'Основное', content: <div className="catalog-editor-sheet"><fieldset className="catalog-editor-fields" disabled={isSaving}><ProductIdentitySection isCreating={isCreating} /><ProductClassificationSection dictionaries={dictionaries} /></fieldset></div> },
          { value: 'properties', title: 'Характеристики', content: <div className="catalog-editor-sheet"><fieldset className="catalog-editor-fields" disabled={isSaving}><ProductPropertiesSection /></fieldset></div> },
          { value: 'photos', title: 'Фотографии', content: <div className="catalog-editor-sheet">{entityId !== undefined ? <CatalogProductMedia productId={entityId} productName={initialValues.product_name} disabled={isSaving} /> : <CatalogFormSection id="photos" title="Фотографии" description="Сначала создайте товар, затем добавьте обложку и дополнительные фото."><button className="catalog-button" disabled={isSaving} type="submit" onClick={() => { shouldOpenPhotos.current = true; }}>Создать товар и загрузить фото</button></CatalogFormSection>}</div> },
          ...(entityId === undefined ? [] : [{ value: 'offers', title: 'Предложения', content: tab === 'offers' ? <CatalogProductOffers productId={entityId} unit={dictionaries.product_units.find((unit) => unit.id === Number(form.getValues('product_unit_id')))?.name || 'ед.'} /> : null }]),
        ]} />
      </form>
    </FormProvider>
  );
}
